import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "components/common";
import { fetchTestSign, submitTestSign } from "./testSignApi";

const money = (v) => (v == null ? "—" : Number(v).toLocaleString("en-US", { maximumFractionDigits: 2 }));
const errorOf = (e, fallback) => e?.response?.data?.error || fallback;

/**
 * Hub-Sign test mode (App Settings → Hub-Sign → HUB_SIGN_TEST_BYPASS). The
 * [Sign Required] email links here instead of Hub-Sign. Opening the page only reads;
 * the step is signed when the button is pressed, so mail scanners that open links
 * ahead of the reader cannot sign anything.
 */
const TestSignPage = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState({ loading: !!token, data: null, error: token ? "" : "This signing link is incomplete." });
  const [result, setResult] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    fetchTestSign(token)
      .then((res) => setState({ loading: false, data: res.data, error: "" }))
      .catch((e) => setState({ loading: false, data: null, error: errorOf(e, "This signing link is invalid or has expired.") }));
  }, [token]);

  const sign = async () => {
    setBusy(true);
    try {
      const res = await submitTestSign(token);
      setState((s) => ({ ...s, data: res.data }));
      setResult(res.message || "Signed.");
    } catch (e) {
      setState((s) => ({ ...s, data: e?.response?.data?.data || s.data, error: errorOf(e, "Could not sign.") }));
    } finally {
      setBusy(false);
    }
  };

  const d = state.data;
  return (
    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 tw-shadow-sm tw-p-6 tw-space-y-4">
      <p className="tw-m-0 tw-inline-block tw-rounded tw-bg-[#fef2f2] tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-text-[#b91c1c]">
        Test mode — Hub-Sign is bypassed. The form gets a "TEST SIGNATURE" stamp.
      </p>
      {state.loading && <p className="tw-m-0 tw-text-sm tw-text-slate-500">Loading…</p>}
      {state.error && <p role="alert" className="tw-m-0 tw-text-sm tw-text-[#b42318]">{state.error}</p>}
      {d && (
        <>
          <div>
            <h1 className="tw-m-0 tw-text-lg tw-font-semibold tw-text-slate-900">{d.code}</h1>
            <p className="tw-m-0 tw-text-sm tw-text-slate-600">
              {d.payee} · {money(d.total_amount)} VND · {d.status}
            </p>
          </div>
          <p className="tw-m-0 tw-text-sm tw-text-slate-700">
            Signing step: <strong>{d.role}</strong> — {d.approver_name || d.approver_email}
          </p>
          {result && <p role="status" className="tw-m-0 tw-text-sm tw-font-semibold tw-text-[#067647]">{result}</p>}
          {d.signable && !result && (
            <Button variant="primary" size="md" disabled={busy} onClick={sign}>
              {busy ? "Signing…" : "Sign (test mode)"}
            </Button>
          )}
          {!d.signable && !result && !state.error && (
            <p className="tw-m-0 tw-text-sm tw-text-slate-600">This step is no longer waiting for this signature.</p>
          )}
          <p className="tw-m-0 tw-text-sm">
            <Link to={`/payment-requests/${d.id}`} className="tw-text-[#0131a6] tw-font-semibold">
              Open {d.code} in the Portal
            </Link>
            <span className="tw-text-slate-500"> (sign-in required)</span>
          </p>
        </>
      )}
    </div>
  );
};

export default TestSignPage;
